import React, { FC } from 'react';
import garageUserService from '../../../services/api/garage-user/garageUserService';
import {
  MAX_RESULTS_GARAGE_USERS,
  USER_ROLES,
} from '../../../constants/uiConstants';
import './garage-users-table.scss';
import { DropdownMenuButton, WarningAlert } from '@epam/promo';
import { CustomDataTable } from '../../../component/data-table/custom-data-table';
import { Dropdown } from '@epam/uui-components';
import {
  DataColumnProps,
  useUuiContext,
  FlexCell,
  FlexRow,
  Text,
  LinkButton,
  DropdownBodyProps,
  DropdownMenuBody,
} from '@epam/uui';
import { RoleType, UserInfo } from '../../../services/api/user/userInfo';

import { ToggleRole } from './toggle-role';
import { useQuery } from '@tanstack/react-query';
import permissionService from '../../../services/api/permission/permissionService';
import { useUserInfo } from '../../../services/auth-client/user-info-provider';
import { QUERY_KEYS } from '../../../constants/queryKeys';
import { DefaultError } from 'component/notification/DefaultError';
import { UserCard } from './user-card';
import { useCloseBlocker } from 'hooks/useCloseBlocker';
import { EditEventModal } from '../../events/components/EventCardHeader';
import { useTimezones } from 'services/timezone';
import useNotificationsModal from 'component/NotifiactionsModal';
import { IEvent } from 'modules/events/models/interfaces';
import eventManagementService from 'services/api/event-managment/eventManagementService';
import {
  isUserWithEditorRole,
  isUserWithEventOrganizerRole,
} from 'utils/rolesUtils';

type Props = {};

export const GarageUsersTable: FC<Props> = () => {
  const svc = useUuiContext();
  const { userInfo } = useUserInfo();
  const { uuiModals } = useUuiContext();
  const { timezones } = useTimezones();
  const { successNotification } = useNotificationsModal();
  const { data: admins } = useQuery(
    [QUERY_KEYS.USERS.SPECIFIED_USER_ROLE],
    () => permissionService.getUsersWithSpecifiedPermission('ADMIN'),
    {
      refetchOnWindowFocus: false,
      onError: (error: any) => {
        useCloseBlocker(svc);
        svc.uuiNotifications
          .show((props) => (
            <DefaultError notificationProps={props} error={error} />
          ))
          .catch(() => null);
      },
    }
  );

  const hasRole = (roles: RoleType[], role: RoleType) =>
    !!roles.find((r) => r == role);

  const AM_I_ADMIN = hasRole(userInfo?.roles || [], USER_ROLES.ADMIN);

  const AM_I_SUPER_ADMIN = hasRole(
    userInfo?.roles || [],
    USER_ROLES.SUPER_ADMIN
  );
  const isEditor: boolean = isUserWithEditorRole(userInfo);
  const isEventOrganizer: boolean = isUserWithEventOrganizerRole(userInfo);
  const editorSwitch = localStorage.getItem('editorSwitch');
  const isEventClickable =
    (isEditor || isEventOrganizer) && !JSON.parse(editorSwitch as string);

  const formatDate = (date: Date): string => {
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `  ${day}/${month}/${year} `;
  };

  const editEventNotification = (message: string) => {
    successNotification(message);
  };

  const onEventClick = async (id: number) => {
    const eventData: IEvent = (await eventManagementService.getEvent(
      id
    )) as IEvent;
    uuiModals
      .show<string>((props) => (
        <EditEventModal {...props} event={eventData} timezones={timezones} />
      ))
      .then((val) =>
        editEventNotification(
          `You have successfully updated the event "${val}".`
        )
      )
      .catch(() => null);
  };

  const renderDropsdownBody = (
    props: DropdownBodyProps,
    challenges: IEvent[]
  ) => {
    return (
      <DropdownMenuBody {...props} cx="event-organizer-dropdown-container">
        {challenges.map((item: IEvent) => {
          return (
            <DropdownMenuButton
              caption={
                <FlexRow alignItems="top">
                  <FlexCell width="100%">
                    <Text
                      lineHeight="18"
                      fontSize="14"
                      color="primary"
                      cx="event-organizer-dropdown-eventname-text"
                    >
                      {item?.name}
                    </Text>
                    <Text
                      lineHeight="18"
                      fontSize="14"
                      color="secondary"
                      cx="event-organizer-dropdown-eventexpdate-text"
                    >
                      Permissions exp. date:
                      {item.plannedEndDate
                        ? formatDate(new Date(item.plannedEndDate))
                        : ''}
                    </Text>
                  </FlexCell>
                </FlexRow>
              }
              isDisabled={isEventClickable}
              onClick={() => onEventClick(item.id)}
            />
          );
        })}
      </DropdownMenuBody>
    );
  };

  const columns: DataColumnProps<UserInfo>[] = [
    {
      key: 'name',
      caption: 'Name',
      render: (item) => <UserCard key={item.id} {...item} />,
      grow: 1,
      width: 250,
    },
    {
      key: 'super_admin',
      caption: 'SUPER ADMIN',
      render: (item) => (
        <ToggleRole
          customDisableTooltipText={
            !hasRole(item.roles, USER_ROLES.ADMIN) && AM_I_SUPER_ADMIN
              ? 'You cannot transfer the role of Super Admin to non-Admins.'
              : AM_I_SUPER_ADMIN
              ? 'You cannot revoke your Admin rights until you transfer your role to another Admin'
              : undefined
          }
          disabled={
            !AM_I_SUPER_ADMIN ||
            hasRole(item.roles, USER_ROLES.SUPER_ADMIN) ||
            !hasRole(item.roles, USER_ROLES.ADMIN)
          }
          roleColumn="SUPER_ADMIN"
          user={item}
          admins={admins}
          minimumRole="Super Admin"
        />
      ),
      width: 150,
      grow: 0,
      shrink: 0,
    },
    {
      key: 'admin',
      caption: 'ADMIN',
      render: (item) => (
        <ToggleRole
          disabled={
            (admins &&
              admins.length <= 2 &&
              AM_I_ADMIN &&
              hasRole(item.roles, USER_ROLES.ADMIN)) ||
            !AM_I_ADMIN ||
            hasRole(item.roles, USER_ROLES.SUPER_ADMIN)
          }
          customDisableTooltipText={
            admins && admins.length <= 2
              ? "It's recommended to have at least 2 Admins. Add new Admins before revoking rights from other Admins"
              : AM_I_SUPER_ADMIN
              ? 'You cannot revoke your Admin rights until you transfer your role to another Admin'
              : hasRole(item.roles, USER_ROLES.SUPER_ADMIN)
              ? 'You cannot assign or revoke this role because you are not a Super Admin'
              : undefined
          }
          roleColumn="ADMIN"
          user={item}
          admins={admins}
          minimumRole="Admin"
        />
      ),
      width: 150,
      grow: 0,
      shrink: 0,
    },
    {
      key: 'editor',
      caption: 'EDITOR',
      render: (item) => (
        <ToggleRole
          disabled={!AM_I_ADMIN}
          roleColumn="EDITOR"
          user={item}
          admins={admins}
          minimumRole="Admin"
        />
      ),
      width: 150,
      grow: 0,
      shrink: 0,
    },
    {
      key: 'event_organizer',
      caption: 'EVENT ORGANIZER',
      render: (item) => (
        <>
          <ToggleRole
            customDisableTooltipText={
              hasRole(item.roles, USER_ROLES.EDITOR) ||
              hasRole(item.roles, USER_ROLES.ADMIN) ||
              hasRole(item.roles, USER_ROLES.SUPER_ADMIN)
                ? 'You can modify permission settings by accessing the specific event.'
                : undefined
            }
            disabled={true}
            roleColumn="EVENT_ORGANIZER"
            user={item}
            admins={admins}
            minimumRole="Editor"
          />
          <Dropdown
            renderBody={(props) =>
              renderDropsdownBody(props, item.challenges as IEvent[])
            }
            renderTarget={(props) => (
              <LinkButton
                caption={`${item.challenges.length} Events`}
                size="30"
                cx="dropdown-render-text"
                {...props}
              />
            )}
            placement="bottom-end"
          />
        </>
      ),
      width: 150,
      grow: 0,
      shrink: 0,
    },
  ];

  return (
    <div className="garage-users-table">
      {admins && admins.length <= 1 && (
        <WarningAlert cx="warning-alert">
          <Text fontSize="14" size="24">
            It&apos;s highly recommended to have more than one Admin in the
            system. Please assign another one.
          </Text>
        </WarningAlert>
      )}

      <CustomDataTable
        key={JSON.stringify(userInfo?.roles || [])}
        fetch={async ({ page, keyword }) => {
          const { userList, totalNumber } =
            await garageUserService.getListUsers({
              amount: MAX_RESULTS_GARAGE_USERS,
              startPoint: (page - 1) * MAX_RESULTS_GARAGE_USERS,
              searchPhrase: encodeURIComponent(keyword || ''),
            });

          return { total: totalNumber, data: userList };
        }}
        queryKey={QUERY_KEYS.USERS.USER_LIST}
        itemPerPage={MAX_RESULTS_GARAGE_USERS}
        filter={{
          amount: MAX_RESULTS_GARAGE_USERS,
        }}
        columns={columns}
        allowSearch
        placeholder="Search by name"
      />
    </div>
  );
};
